import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import clsx from "clsx";
import { twMerge } from "tailwind-merge";
import SettingsCommonLayout from "@/features/settings/SettingsCommonLayout";
import FieldsCard from "@/features/settings/components/FieldsCard";
import CommonInput from "@/shared/components/ui/Input/CommonInput";
import { ChipInput } from "@/shared/components/ui/Input/chipInput";
import { companyProfileSchema } from "../../../../schema/OnBoarding";
import { useMainStore } from "@/stores";
import { showErrorFromMutation } from "@/shared/utils/toast";
import {
  useCompanyProfile,
  useUpdateCompanyProfile,
} from "@/hooks/useCompanyProfile";
import Loader from "@/shared/components/ui/Loaders/Loader";
import { USER_ROLES } from "@/shared/utils/constants";

const cn = (...inputs) => twMerge(clsx(inputs));

export default function CompanyProfile({ tab = "" }) {
  const { companyProfile, setCompanyProfile, getCompanySubmitData, user } =
    useMainStore();

  const canEditCompanyProfile = user?.role === USER_ROLES.ORG_ADMIN;

  const { data: companyProfileData, isLoading: isCompanyProfileLoading } =
    useCompanyProfile();
  const {
    mutateAsync: updateCompanyProfile,
    isPending: isUpdateCompanyProfilePending,
  } = useUpdateCompanyProfile();

  const companyDefaults = useMemo(
    () => ({
      website_url: companyProfileData?.website_url || "",
      description: companyProfileData?.summary || "",
      services: Array.isArray(companyProfileData?.services)
        ? companyProfileData?.services
        : [],
    }),
    [
      companyProfileData?.website_url,
      companyProfileData?.summary,
      companyProfileData?.services,
    ],
  );

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    mode: "all",
    resolver: zodResolver(companyProfileSchema),
    defaultValues: companyDefaults,
  });

  const services = watch("services");

  useEffect(() => {
    reset(companyDefaults);
    setCompanyProfile({
      website_url: companyDefaults.website_url,
      description: companyDefaults.description,
      services: companyDefaults.services,
      company_services: companyDefaults.services,
    });
  }, [companyDefaults, reset, setCompanyProfile]);

  const onSave = handleSubmit(async (values) => {
    if (!canEditCompanyProfile) return;
    setCompanyProfile({
      website_url: values.website_url,
      description: values.description,
      services: values.services,
    });
    try {
      await updateCompanyProfile({ ...getCompanySubmitData() });
      reset(values);
    } catch (err) {
      showErrorFromMutation(err, "Could not save company profile.");
    }
  });

  const handleReset = () => {
    reset(companyDefaults);
  };

  return (
    <SettingsCommonLayout
      title="Company profile"
      description={
        canEditCompanyProfile
          ? "Review and update how SalesWorx describes your company for personalization."
          : "How SalesWorx describes your company for personalization. Only an organization admin can edit this profile."
      }
      tab={tab}
      cancelButtonClick={canEditCompanyProfile ? handleReset : undefined}
      saveButtonClick={canEditCompanyProfile ? onSave : undefined}
      saveDisabled={!isDirty}
      cancelDisabled={!isDirty}
      isSubmitting={isUpdateCompanyProfilePending}
    >
      {isCompanyProfileLoading ? (
        <Loader text="Loading company profile..." />
      ) : (
        <div className="mt-4 sm:mt-5 w-full max-w-5xl mx-auto">
          <div className="space-y-0">
            {/* Website */}
            <FieldsCard
              title="Website"
              description="Your company domain (no https:// needed)."
            >
              <div className="w-full">
                <CommonInput
                  {...register("website_url")}
                  placeholder="example.com"
                  disabled={!canEditCompanyProfile}
                  className={cn(
                    "w-full border-gray-300"
                  )}
                  inputClassName="text-gray-500"
                />
              </div>
            </FieldsCard>

            {/* Description */}
            <FieldsCard
              title="Description"
              description="Short summary shown to AI when drafting outreach."
            >
              <div className="w-full">
                <textarea
                  {...register("description")}
                  className={cn(
                    "w-full rounded-lg border px-3 py-2",
                    "text-sm sm:text-[13px] md:text-sm",
                    "min-h-[120px] sm:min-h-[140px] md:min-h-[160px]",
                    "resize-y outline-none transition-colors shadow-sm",
                    "border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-100",
                    "placeholder:text-gray-500 dark:placeholder:text-gray-400",
                    "focus:border-secondary focus:border-[1.5px]",
                    "disabled:cursor-not-allowed disabled:border-gray-200 dark:disabled:border-gray-800 disabled:text-gray-400 dark:disabled:text-gray-500",
                    errors.description && "border-red-500"
                  )}
                  placeholder="Describe what your company does."
                  disabled={!canEditCompanyProfile}
                />

                {errors.description && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.description.message}
                  </p>
                )}
              </div>
            </FieldsCard>

            {/* Services */}
            <FieldsCard
              title="Services"
              description="Products or services you want campaigns to reference."
              borderBottom={false}
            >
              <div className="w-full">
                <ChipInput
                  suggestions={companyProfile?.company_services || []}
                  defaultChips={services}
                  placeholder="Type a service and press Enter"
                  secondaryPlaceholder="Add another service..."
                  dropdownOptions={[]}
                  px="px-3"
                  py="py-2"
                  onChange={(newServices) => {
                    setValue("services", newServices, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }}
                  errors={errors.services}
                  disabled={!canEditCompanyProfile}
                />

                {errors.services && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.services.message}
                  </p>
                )}
              </div>
            </FieldsCard>
          </div>
        </div>
      )}
    </SettingsCommonLayout>
  );
}
